import React from 'react';

export interface AlertData {
    alertTitle?: string;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
    open: boolean;
    autoHideDuration?: number;
    isModal?: boolean;
    style?: React.CSSProperties;
}

interface AlertContextValue {
    alert: AlertData;
    setAlert: (alert: AlertData) => void;
    handleClose: () => void;
}

const AlertContext = React.createContext<AlertContextValue>({
    alert: {
        alertTitle: '',
        message: '',
        severity: 'success',
        autoHideDuration: 5000,
        isModal: false,
        open: false,
        style: {},
    },
    setAlert: () => {
        // Do nothing
    },
    handleClose: () => {
        // Do nothing
    },
});

export const AlertProvider = ({ children }: { children: React.ReactNode }) => {
    const [globalAlert, setGlobalAlert] = React.useState<AlertData>({
        alertTitle: '',
        open: false,
        message: '',
        severity: 'success',
        autoHideDuration: 5000,
        isModal: false,
        style: {},
    });
    const handleClose = React.useCallback(
        () => setGlobalAlert((prevState) => ({ ...prevState, open: false })),
        [],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    const setAlert = React.useCallback(
        (alertState: AlertData) => {
            setGlobalAlert((prevState) => ({ ...prevState, ...alertState }));
        },
        [setGlobalAlert],
    );

    return (
        <AlertContext.Provider value={{ alert: globalAlert, setAlert, handleClose }}>
            {children}
        </AlertContext.Provider>
    );
};

export default AlertContext;
